import { useEffect, useMemo, useRef } from 'react';

import { useTracking } from '@change-corgi/core/react/tracking';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { PERSONALIZED_TASKS_GENERATION_STATUSES } from '../../constants';

import type { ParsedPersonalizedTasks } from './taskShapes';

const AI_GUIDANCE_PROMPT_FAILS_EVENT = 'ai_guidance_prompt_fails';
const AI_GUIDANCE_PROMPT_SUCCEEDS_EVENT = 'ai_guidance_prompt_succeeds';
const AI_GUIDANCE_VERSION = 'new_ai_03_2026';

type AiGuidanceTerminalEvent =
	| Readonly<{
			key: 'COMPLETED';
			trackingEventName: typeof AI_GUIDANCE_PROMPT_SUCCEEDS_EVENT;
			errorReport: null;
	  }>
	| Readonly<{
			key: 'FAILED';
			trackingEventName: typeof AI_GUIDANCE_PROMPT_FAILS_EVENT;
			errorReport: Readonly<{
				error: Error;
				context: Readonly<{
					slug: string;
					version: typeof AI_GUIDANCE_VERSION;
					generationStatus: string;
					failureContext: ParsedPersonalizedTasks['failureContext'];
				}>;
			}>;
	  }>;

function getAiGuidanceTerminalEvent({
	failureContext,
	generationStatus,
	slug,
}: {
	failureContext: ParsedPersonalizedTasks['failureContext'];
	generationStatus: string;
	slug: string;
}): AiGuidanceTerminalEvent | null {
	if (generationStatus === PERSONALIZED_TASKS_GENERATION_STATUSES.COMPLETED) {
		return {
			key: 'COMPLETED',
			trackingEventName: AI_GUIDANCE_PROMPT_SUCCEEDS_EVENT,
			errorReport: null,
		};
	}

	if (generationStatus === PERSONALIZED_TASKS_GENERATION_STATUSES.FAILED) {
		return {
			key: 'FAILED',
			trackingEventName: AI_GUIDANCE_PROMPT_FAILS_EVENT,
			errorReport: {
				error: new Error('AI guidance generation failed'),
				context: {
					slug,
					version: AI_GUIDANCE_VERSION,
					generationStatus,
					failureContext,
				},
			},
		};
	}

	return null;
}

export function useTrackAiGuidanceGenerationStatus({
	generationStatus,
	failureContext,
	isEnabled,
	slug,
}: {
	generationStatus: string;
	failureContext: ParsedPersonalizedTasks['failureContext'];
	isEnabled: boolean;
	slug: string;
}): void {
	const track = useTracking();
	const utilityContext = useUtilityContext();
	const trackedEventKeyRef = useRef<AiGuidanceTerminalEvent['key'] | null>(null);
	const terminalEvent = useMemo(
		() =>
			getAiGuidanceTerminalEvent({
				failureContext,
				generationStatus,
				slug,
			}),
		[failureContext, generationStatus, slug],
	);

	useEffect(() => {
		if (!isEnabled || !terminalEvent || trackedEventKeyRef.current === terminalEvent.key) {
			return;
		}

		trackedEventKeyRef.current = terminalEvent.key;

		if (terminalEvent.errorReport) {
			void utilityContext.errorReporter.report(terminalEvent.errorReport, {
				severity: 'warning',
			});
		}

		void track(terminalEvent.trackingEventName, {
			version: AI_GUIDANCE_VERSION,
		});
	}, [isEnabled, terminalEvent, track, utilityContext]);
}
